import React, { useState, useCallback, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
	Button,
	TableBody,
	TableRow,
	TableCell,
	IconButton,
	Table,
	TableHead,
	Paper,
	Tooltip,
	Typography,
	CircularProgress,
	FormControl,
	Select,
	MenuItem,
	Menu,
	ListItemIcon,
	ListItemText,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Divider,
} from "@material-ui/core";
import {
	Edit,
	CheckCircle,
	SignalCellularConnectedNoInternet2Bar,
	SignalCellularConnectedNoInternet0Bar,
	SignalCellular4Bar,
	CropFree,
	DeleteOutline,
	Chat,
	InfoOutlined,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { ConnectionsContext } from "../../context/Connections/ConnectionsContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import {
	WhatsAppConnectionIcon,
	MessengerConnectionIcon,
	InstagramConnectionIcon,
	WebChatConnectionIcon,
} from "../../components/ConnectionChannelIcon";

const useStyles = makeStyles(theme => ({
	mainPaper: {
		flex: 1,
		padding: theme.spacing(1),
		overflowY: "auto",
		margin: 20,
		...theme.scrollbarStyles,
		[theme.breakpoints.down("xs")]: {
			margin: theme.spacing(1),
			padding: theme.spacing(1),
			border: 0,
			boxShadow: "none",
			backgroundColor: "transparent",
		},
	},
	tableWrapper: {
		[theme.breakpoints.down("xs")]: {
			display: "none",
		},
	},
	mobileCards: {
		display: "none",
		[theme.breakpoints.down("xs")]: {
			display: "grid",
			gap: theme.spacing(1.5),
		},
	},
	mobileCard: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 8,
		backgroundColor: theme.palette.background.paper,
		padding: theme.spacing(1.5),
	},
	mobileCardHeader: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	mobileConnectionName: {
		fontWeight: 600,
		lineHeight: 1.25,
		wordBreak: "break-word",
	},
	mobileConnectionNumber: {
		color: theme.palette.text.secondary,
		fontSize: 13,
		lineHeight: 1.35,
		wordBreak: "break-word",
	},
	mobileDefaultBadge: {
		display: "inline-flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
		color: green[700],
		fontSize: 12,
		fontWeight: 600,
		whiteSpace: "nowrap",
	},
	mobileCardGrid: {
		display: "grid",
		gridTemplateColumns: "1fr",
		gap: theme.spacing(1.25),
		marginTop: theme.spacing(1.25),
	},
	mobileField: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		minHeight: 32,
	},
	mobileFieldStacked: {
		alignItems: "flex-start",
		flexDirection: "column",
	},
	mobileLabel: {
		color: theme.palette.text.secondary,
		fontSize: 12,
		fontWeight: 600,
		textTransform: "uppercase",
	},
	mobileValue: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		flexWrap: "wrap",
		textAlign: "right",
		minWidth: 0,
		"& .MuiFormControl-root": {
			minWidth: 132,
		},
		"& .MuiButton-root": {
			marginLeft: theme.spacing(0.5),
			marginBottom: theme.spacing(0.5),
		},
	},
	mobileActions: {
		display: "flex",
		justifyContent: "flex-end",
		gap: theme.spacing(0.5),
		marginTop: theme.spacing(1),
	},
	customTableCell: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	tooltip: {
		backgroundColor: "#f5f5f9",
		color: "rgba(0, 0, 0, 0.87)",
		fontSize: theme.typography.pxToRem(14),
		border: "1px solid #dadde9",
		maxWidth: 450,
	},
	tooltipPopper: {
		textAlign: "center",
	},
	buttonProgress: {
		color: green[500],
	},
	connectionTypeIcon: {
		minWidth: 32,
		marginRight: 10,
	},
	connectionTypeSvgIcon: {
		width: 32,
		height: 32,
		display: "block",
	},
	connectionTypeItemText: {
		"& .MuiListItemText-primary": {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(0.75),
			fontWeight: 500,
		},
	},
	platformCell: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		gap: theme.spacing(1),
		whiteSpace: "nowrap",
	},
	platformIcon: {
		width: 30,
		height: 30,
		display: "block",
		flexShrink: 0,
	},
	platformLabel: {
		fontWeight: 500,
	},
	mobilePlatform: {
		marginTop: theme.spacing(0.75),
		justifyContent: "flex-start",
	},
}));

const CustomToolTip = ({ title, content, children }) => {
	const classes = useStyles();

	return (
		<Tooltip
			arrow
			classes={{
				tooltip: classes.tooltip,
				popper: classes.tooltipPopper,
			}}
			title={
				<React.Fragment>
					<Typography gutterBottom color="inherit">
						{title}
					</Typography>
					{content && <Typography>{content}</Typography>}
				</React.Fragment>
			}
		>
			{children}
		</Tooltip>
	);
};

const activeConnectionStatuses = [
	"CONNECTED",
	"OPENING",
	"qrcode",
	"PAIRING",
	"TIMEOUT",
];

const isMetaChannel = channel => ["messenger", "instagram"].includes(channel);

const connectionTypeOptions = [
	{
		channel: "whatsapp",
		label: "WhatsApp",
		description:
			"Conexion tradicional via QR Code usando WhatsApp personal o Business.",
		Icon: WhatsAppConnectionIcon,
	},
	{
		channel: "messenger",
		label: "Messenger",
		description:
			"Canal de Messenger conectado a una pagina de Facebook.",
		Icon: MessengerConnectionIcon,
	},
	{
		channel: "instagram",
		label: "Instagram",
		description:
			"Canal de mensajes directos de Instagram profesional.",
		Icon: InstagramConnectionIcon,
	},
	{
		channel: "webchat",
		label: "Web Chat",
		description:
			"Widget propio para recibir conversaciones desde una web.",
		Icon: WebChatConnectionIcon,
	},
];

const isConnectionActive = whatsApp =>
	((whatsApp.channel === "webchat" || isMetaChannel(whatsApp.channel)) &&
		whatsApp.status === "CONNECTED") ||
	(whatsApp.channel !== "webchat" &&
		!isMetaChannel(whatsApp.channel) &&
		activeConnectionStatuses.includes(whatsApp.status)) ||
	(whatsApp.channel !== "webchat" &&
		!isMetaChannel(whatsApp.channel) &&
		whatsApp.status === "DISCONNECTED" &&
		(Boolean(whatsApp.number) || Boolean(whatsApp.session)));

const formatConnectionExpiration = expiresAt => {
	if (!expiresAt) return "Sin activar";

	const expirationDate = new Date(expiresAt);
	if (Number.isNaN(expirationDate.getTime())) return "Sin activar";

	const startOfToday = new Date();
	startOfToday.setHours(0, 0, 0, 0);
	const startOfExpiration = new Date(expirationDate);
	startOfExpiration.setHours(0, 0, 0, 0);
	const remainingDays = Math.ceil(
		(startOfExpiration.getTime() - startOfToday.getTime()) / 86400000
	);
	const formattedDate = expirationDate.toLocaleDateString("es-CR");

	if (remainingDays < 0) return `Vencida ${formattedDate}`;
	return `Vence ${formattedDate} - quedan ${remainingDays} dias`;
};

const isConnectionExpired = whatsApp => {
	if (!whatsApp?.expiresAt) return false;

	const expirationDate = new Date(whatsApp.expiresAt);
	if (Number.isNaN(expirationDate.getTime())) return false;

	return expirationDate.getTime() < Date.now();
};

const FACEBOOK_SDK_SCRIPT_ID = "facebook-jssdk";

const loadFacebookSdk = ({ appId, version }) =>
	new Promise((resolve, reject) => {
		if (window.FB) {
			window.FB.init({
				appId,
				cookie: true,
				xfbml: false,
				version,
			});
			resolve(window.FB);
			return;
		}

		const existingScript = document.getElementById(FACEBOOK_SDK_SCRIPT_ID);
		const previousAsyncInit = window.fbAsyncInit;

		window.fbAsyncInit = () => {
			if (typeof previousAsyncInit === "function") previousAsyncInit();
			window.FB.init({
				appId,
				cookie: true,
				xfbml: false,
				version,
			});
			resolve(window.FB);
		};

		if (existingScript) return;

		const script = document.createElement("script");
		script.id = FACEBOOK_SDK_SCRIPT_ID;
		script.async = true;
		script.defer = true;
		script.crossOrigin = "anonymous";
		script.src = "https://connect.facebook.net/en_US/sdk.js";
		script.onerror = () => reject(new Error("ERR_FACEBOOK_SDK_LOAD"));
		document.body.appendChild(script);
	});

const Connections = () => {
	const classes = useStyles();

	const { connections, loading } = useContext(ConnectionsContext);
	const { user } = useContext(AuthContext);
	const canManageConnections =
		["admin", "superadmin", "owner"].includes(user.profile);
	const canDeleteConnections = ["superadmin", "owner"].includes(user.profile);
	const canUseMetaConnections = ["superadmin", "owner"].includes(user.profile);
	const hideResellerControls = Boolean(user?.company?.resellerBranding);
	const isResellerCustomer = Boolean(user?.company?.resellerCompanyId);
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [connectionTypeAnchorEl, setConnectionTypeAnchorEl] = useState(null);
	const [newConnectionChannel, setNewConnectionChannel] = useState("whatsapp");
	const [facebookPageModalOpen, setFacebookPageModalOpen] = useState(false);
	const [facebookOAuthNonce, setFacebookOAuthNonce] = useState("");
	const [facebookPages, setFacebookPages] = useState([]);
	const [selectedFacebookPageId, setSelectedFacebookPageId] = useState("");
	const [loadingMetaAuth, setLoadingMetaAuth] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
		open: false,
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(
		confirmationModalInitialState
	);

	const handleStartConnectionSession = async whatsAppId => {
		try {
			await api.post(`/connection-session/${whatsAppId}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleRequestNewQrCode = async whatsAppId => {
		try {
			await api.put(`/connection-session/${whatsAppId}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConnectionTypeMenu = event => {
		setConnectionTypeAnchorEl(event.currentTarget);
	};

	const handleCloseConnectionTypeMenu = () => {
		setConnectionTypeAnchorEl(null);
	};

	const handleSelectConnectionType = channel => {
		if (isMetaChannel(channel)) {
			if (!canUseMetaConnections) {
				handleCloseConnectionTypeMenu();
				return;
			}
			handleCloseConnectionTypeMenu();
			startMetaOAuth(channel);
			return;
		}

		setNewConnectionChannel(channel);
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
		handleCloseConnectionTypeMenu();
	};

	const startMetaOAuth = async (channel, connectionId) => {
		try {
			setLoadingMetaAuth(true);
			if (channel === "messenger") {
				const { data: sdkConfig } = await api.get(
					"/connections/meta/facebook-sdk-config"
				);
				const FB = await loadFacebookSdk(sdkConfig);
				const loginResponse = await new Promise(resolve => {
					FB.login(response => resolve(response), {
						scope: sdkConfig.scopes.join(","),
						auth_type: "rerequest",
					});
				});

				if (
					loginResponse.status !== "connected" ||
					!loginResponse.authResponse?.accessToken
				) {
					throw new Error("Autorizacion de Facebook cancelada o incompleta");
				}

				const { data } = await api.post("/connections/meta/facebook-pages", {
					accessToken: loginResponse.authResponse.accessToken,
					connectionId,
				});
				setFacebookOAuthNonce(data.nonce);
				setFacebookPages(data.pages || []);
				setSelectedFacebookPageId(data.pages?.[0]?.id || "");
				setFacebookPageModalOpen(true);
				if (!data.pages?.length) {
					toast.warn("Facebook no devolvio paginas para esta cuenta.");
				}
				setLoadingMetaAuth(false);
				return;
			}

			const { data } = await api.post("/connections/meta/oauth-url", {
				channel,
				connectionId,
			});
			window.location.href = data.url;
		} catch (err) {
			setLoadingMetaAuth(false);
			toastError(err);
		}
	};

	const loadFacebookOAuthResult = useCallback(async nonce => {
		try {
			setLoadingMetaAuth(true);
			const { data } = await api.get(`/connections/meta/oauth-result/${nonce}`);
			setFacebookOAuthNonce(nonce);
			setFacebookPages(data.pages || []);
			setSelectedFacebookPageId(data.pages?.[0]?.id || "");
			setFacebookPageModalOpen(true);
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingMetaAuth(false);
		}
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const metaOAuth = params.get("metaOAuth");
		const status = params.get("status");
		const nonce = params.get("nonce");
		const message = params.get("message");

		if (!metaOAuth) return;

		window.history.replaceState({}, document.title, window.location.pathname);

		if (status === "success") {
			toast.success("Conexion Meta agregada correctamente");
			return;
		}

		if (status === "select_page" && nonce) {
			loadFacebookOAuthResult(nonce);
			return;
		}

		if (status === "error") {
			toast.error(message || "No se pudo completar la autenticacion Meta");
		}
	}, [loadFacebookOAuthResult]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("freeTrialPhoneBlocked", data => {
			toast.warn(
				data.message ||
					"Este numero ya fue utilizado en una prueba gratis anteriormente."
			);
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const handleCompleteFacebookOAuth = async () => {
		if (!selectedFacebookPageId) return;

		try {
			setLoadingMetaAuth(true);
			await api.post("/connections/meta/oauth-complete", {
				nonce: facebookOAuthNonce,
				pageId: selectedFacebookPageId,
			});
			toast.success("Conexion de Facebook agregada correctamente");
			setFacebookPageModalOpen(false);
			setFacebookOAuthNonce("");
			setFacebookPages([]);
			setSelectedFacebookPageId("");
		} catch (err) {
			toastError(err);
		} finally {
			setLoadingMetaAuth(false);
		}
	};

	const handleOpenWhatsAppModal = () => {
		setNewConnectionChannel("whatsapp");
		setSelectedWhatsApp(null);
		setWhatsAppModalOpen(true);
	};

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, [setSelectedWhatsApp, setWhatsAppModalOpen]);

	const handleOpenQrModal = whatsApp => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleOpenPendingQrModal = async whatsApp => {
		try {
			setSelectedWhatsApp(whatsApp);
			setQrModalOpen(true);
			await handleRequestNewQrCode(whatsApp.id);
		} catch (err) {
			toastError(err);
		}
	};

	const handlePreparePendingQrCode = async whatsApp => {
		try {
			await handleRequestNewQrCode(whatsApp.id);
		} catch (err) {
			toastError(err);
		}
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
	}, [setQrModalOpen, setSelectedWhatsApp]);

	const handleEditWhatsApp = whatsApp => {
		if (isConnectionExpired(whatsApp)) return;

		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const activateConnection = async whatsApp => {
		if (isConnectionExpired(whatsApp)) return;

		if (whatsApp?.channel === "webchat" || isMetaChannel(whatsApp?.channel)) {
			await api.put(`/connections/${whatsApp.id}`, {
				status: "CONNECTED",
				channel: whatsApp.channel,
			});
			return;
		}

		if (whatsApp?.status === "qrcode") {
			return;
		}

		if (whatsApp?.status === "DISCONNECTED" && !whatsApp.number) {
			await handlePreparePendingQrCode(whatsApp);
			return;
		}

		await api.post(`/connection-session/${whatsApp.id}`);
	};

	const activationWillDisconnectAnother = whatsApp => {
		const maxActiveConnections = Number(user?.company?.maxConnections);

		if (
			!Number.isFinite(maxActiveConnections) ||
			maxActiveConnections < 0 ||
			isConnectionActive(whatsApp)
		) {
			return false;
		}

		const activeCount = connections.filter(item => {
			if (item.id === whatsApp.id) return false;
			if (item.channel === "webchat") return false;
			if (isMetaChannel(item.channel)) return false;
			return isConnectionActive(item);
		}).length;

		return activeCount >= maxActiveConnections;
	};

	const handleSetConnectionUsage = async (whatsApp, usage) => {
		if (isConnectionExpired(whatsApp)) return;

		if (usage === "inactive") {
			if (whatsApp.channel === "webchat" || isMetaChannel(whatsApp.channel)) {
				try {
					await api.put(`/connections/${whatsApp.id}`, {
						status: "DISCONNECTED",
						channel: whatsApp.channel,
					});
				} catch (err) {
					toastError(err);
				}
				return;
			}

			if (whatsApp.status === "qrcode") {
				try {
					await api.delete(`/connection-session/${whatsApp.id}?clearSessionData=true`);
				} catch (err) {
					toastError(err);
				}
				return;
			}

			handleOpenConfirmationModal("inactive", whatsApp.id);
			return;
		}

		if (activationWillDisconnectAnother(whatsApp)) {
			handleOpenConfirmationModal("activate", whatsApp.id);
			return;
		}

		try {
			await activateConnection(whatsApp);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: whatsAppId,
			});
		}

		if (action === "delete") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				whatsAppId: whatsAppId,
			});
		}

		if (action === "inactive") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId: whatsAppId,
			});
		}

		if (action === "activate") {
			setConfirmModalInfo({
				action: action,
				title: i18n.t("connections.confirmationModal.activateTitle"),
				message: i18n.t("connections.confirmationModal.activateMessage"),
				whatsAppId: whatsAppId,
			});
		}
		setConfirmModalOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try {
				await api.delete(
					`/connection-session/${confirmModalInfo.whatsAppId}?clearSessionData=true`
				);
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "inactive") {
			try {
				await api.delete(
					`/connection-session/${confirmModalInfo.whatsAppId}?clearSessionData=true`
				);
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "delete") {
			try {
				await api.delete(`/connections/${confirmModalInfo.whatsAppId}`);
				toast.success(i18n.t("connections.toasts.deleted"));
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "activate") {
			try {
				const whatsApp = connections.find(
					item => item.id === confirmModalInfo.whatsAppId
				);

				if (whatsApp) {
					await activateConnection(whatsApp);
				}
			} catch (err) {
				toastError(err);
			}
		}

		setConfirmModalInfo(confirmationModalInitialState);
	};

	const renderActionButtons = whatsApp => {
		if (isConnectionExpired(whatsApp)) {
			return (
				<Button size="small" variant="contained" color="primary" disabled>
					{i18n.t("connections.buttons.qrcode")}
				</Button>
			);
		}

		if (whatsApp.channel === "webchat") {
			return (
				<Button size="small" variant="outlined" disabled>
					Web Chat
				</Button>
			);
		}

		if (isMetaChannel(whatsApp.channel)) {
			return (
				<Button
					size="small"
					variant="outlined"
					color="primary"
					disabled={loadingMetaAuth}
					onClick={() => startMetaOAuth(whatsApp.channel, whatsApp.id)}
				>
					Reautorizar
				</Button>
			);
		}

		const isPendingQr = whatsApp.status === "DISCONNECTED" && !whatsApp.number;
		const activeUsage = isConnectionActive(whatsApp);

		if (!activeUsage) {
			return (
				<Button size="small" variant="contained" color="primary" disabled>
					{i18n.t("connections.buttons.qrcode")}
				</Button>
			);
		}

		return (
			<>
				{whatsApp.status === "qrcode" && (
					<Button
						size="small"
						variant="contained"
						color="primary"
						onClick={() => handleOpenQrModal(whatsApp)}
					>
						{i18n.t("connections.buttons.qrcode")}
					</Button>
				)}
				{isPendingQr && (
					<Button
						size="small"
						variant="contained"
						color="primary"
						onClick={() => handleOpenPendingQrModal(whatsApp)}
					>
						{i18n.t("connections.buttons.qrcode")}
					</Button>
				)}
				{whatsApp.status === "DISCONNECTED" && !isPendingQr && (
					<>
						<Button
							size="small"
							variant="outlined"
							color="primary"
							onClick={() => handleStartConnectionSession(whatsApp.id)}
						>
							{i18n.t("connections.buttons.tryAgain")}
						</Button>{" "}
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							onClick={() => handleRequestNewQrCode(whatsApp.id)}
						>
							{i18n.t("connections.buttons.newQr")}
						</Button>
					</>
				)}
				{(whatsApp.status === "CONNECTED" ||
					whatsApp.status === "PAIRING" ||
					whatsApp.status === "TIMEOUT") && (
					<Can
						role={user.profile}
						perform="connections-page:manageConnections"
						yes={() => (
							<Button
								size="small"
								variant="outlined"
								color="secondary"
								onClick={() => {
									handleOpenConfirmationModal("disconnect", whatsApp.id);
								}}
							>
								{i18n.t("connections.buttons.disconnect")}
							</Button>
						)}
					/>
				)}
				{whatsApp.status === "OPENING" && (
					<Button size="small" variant="outlined" disabled color="default">
						{i18n.t("connections.buttons.connecting")}
					</Button>
				)}
			</>
		);
	};

	const renderUsageSelect = whatsApp => {
		const usage = isConnectionActive(whatsApp) ? "active" : "inactive";
		const expired = isConnectionExpired(whatsApp);

		return (
			<FormControl margin="dense" variant="outlined">
				<Select
					value={usage}
					disabled={expired}
					onChange={event =>
						handleSetConnectionUsage(whatsApp, event.target.value)
					}
				>
					<MenuItem value="active">
						{i18n.t("connections.usage.active")}
					</MenuItem>
					<MenuItem value="inactive">
						{i18n.t("connections.usage.inactive")}
					</MenuItem>
				</Select>
			</FormControl>
		);
	};

	const renderStatusToolTips = whatsApp => {
		if (whatsApp.channel === "webchat" || isMetaChannel(whatsApp.channel)) {
			return (
				<div className={classes.customTableCell}>
					<CustomToolTip
						title={whatsApp.channel === "webchat" ? "Web Chat" : "Meta API"}
					>
						<Chat style={{ color: isConnectionActive(whatsApp) ? green[500] : undefined }} />
					</CustomToolTip>
				</div>
			);
		}

		const isPendingQr = whatsApp.status === "DISCONNECTED" && !whatsApp.number;

		return (
			<div className={classes.customTableCell}>
				{isPendingQr && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.qrcode.title")}
						content={i18n.t("connections.toolTips.qrcode.content")}
					>
						<CropFree />
					</CustomToolTip>
				)}
				{whatsApp.status === "DISCONNECTED" && !isPendingQr && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.disconnected.title")}
						content={i18n.t("connections.toolTips.disconnected.content")}
					>
						<SignalCellularConnectedNoInternet0Bar color="secondary" />
					</CustomToolTip>
				)}
				{whatsApp.status === "OPENING" && (
					<CircularProgress size={24} className={classes.buttonProgress} />
				)}
				{whatsApp.status === "qrcode" && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.qrcode.title")}
						content={i18n.t("connections.toolTips.qrcode.content")}
					>
						<CropFree />
					</CustomToolTip>
				)}
				{whatsApp.status === "CONNECTED" && (
					<CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
						<SignalCellular4Bar style={{ color: green[500] }} />
					</CustomToolTip>
				)}
				{(whatsApp.status === "TIMEOUT" || whatsApp.status === "PAIRING") && (
					<CustomToolTip
						title={i18n.t("connections.toolTips.timeout.title")}
						content={i18n.t("connections.toolTips.timeout.content")}
					>
						<SignalCellularConnectedNoInternet2Bar color="secondary" />
					</CustomToolTip>
				)}
			</div>
		);
	};

	const getConnectionLabel = whatsApp => {
		if (whatsApp.channel === "webchat") return "Web Chat";
		if (whatsApp.channel === "messenger") return whatsApp.number || "Messenger";
		if (whatsApp.channel === "instagram") return whatsApp.number || "Instagram";
		return whatsApp.number || "WhatsApp";
	};

	const renderPlatformCell = (whatsApp, className = "") => {
		const option =
			connectionTypeOptions.find(item => item.channel === whatsApp.channel) ||
			connectionTypeOptions[0];
		const PlatformIcon = option.Icon;

		return (
			<Tooltip title={option.label}>
				<div className={`${classes.platformCell} ${className}`}>
					<PlatformIcon className={classes.platformIcon} />
					<span className={classes.platformLabel}>{option.label}</span>
				</div>
			</Tooltip>
		);
	};

	const renderMobileConnectionCard = whatsApp => (
		<div className={classes.mobileCard} key={whatsApp.id}>
			<div className={classes.mobileCardHeader}>
				<div>
					{renderPlatformCell(whatsApp, classes.mobilePlatform)}
					<Typography className={classes.mobileConnectionName}>
						{whatsApp.name}
					</Typography>
					<Typography className={classes.mobileConnectionNumber}>
						{getConnectionLabel(whatsApp)}
					</Typography>
				</div>
				{!hideResellerControls && whatsApp.isDefault && (
					<div className={classes.mobileDefaultBadge}>
						<CheckCircle fontSize="small" />
						{i18n.t("connections.table.default")}
					</div>
				)}
			</div>

			<Divider />

			<div className={classes.mobileCardGrid}>
				<div className={classes.mobileField}>
					<span className={classes.mobileLabel}>
						{i18n.t("connections.table.status")}
					</span>
					<div className={classes.mobileValue}>
						{renderStatusToolTips(whatsApp)}
					</div>
				</div>

				{canManageConnections && (
					<div className={classes.mobileField}>
						<span className={classes.mobileLabel}>
							{i18n.t("connections.table.usage")}
						</span>
						<div className={classes.mobileValue}>
							{renderUsageSelect(whatsApp)}
						</div>
					</div>
				)}

				{canManageConnections && (
					<div className={classes.mobileField}>
						<span className={classes.mobileLabel}>
							{i18n.t("connections.table.session")}
						</span>
						<div className={classes.mobileValue}>
							{renderActionButtons(whatsApp)}
						</div>
					</div>
				)}

				{isResellerCustomer && (
					<div className={classes.mobileField}>
						<span className={classes.mobileLabel}>Vencimiento</span>
						<Typography variant="body2">
							{formatConnectionExpiration(whatsApp.expiresAt)}
						</Typography>
					</div>
				)}

				<div className={classes.mobileField}>
					<span className={classes.mobileLabel}>
						{i18n.t("connections.table.lastUpdate")}
					</span>
					<Typography variant="body2">
						{format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
					</Typography>
				</div>
			</div>

			{canManageConnections && (
				<div className={classes.mobileActions}>
					<IconButton
						size="small"
						disabled={isConnectionExpired(whatsApp)}
						onClick={() => handleEditWhatsApp(whatsApp)}
					>
						<Edit />
					</IconButton>

					{canDeleteConnections && (
						<IconButton
							size="small"
							disabled={isConnectionExpired(whatsApp)}
							onClick={() => {
								handleOpenConfirmationModal("delete", whatsApp.id);
							}}
						>
							<DeleteOutline />
						</IconButton>
					)}
				</div>
			)}
		</div>
	);

	return (
		<MainContainer>
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
				initialChannel={newConnectionChannel}
			/>
			<Dialog
				open={facebookPageModalOpen}
				onClose={() => setFacebookPageModalOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Seleccionar pagina de Facebook</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						Selecciona la pagina que quieres conectar como canal de Messenger.
					</Typography>
					<TextField
						select
						label="Pagina"
						variant="outlined"
						margin="dense"
						fullWidth
						value={selectedFacebookPageId}
						onChange={event => setSelectedFacebookPageId(event.target.value)}
					>
						{facebookPages.map(page => (
							<MenuItem key={page.id} value={page.id}>
								{page.name}
								{page.instagram?.username
									? ` - Instagram: ${page.instagram.username}`
									: ""}
							</MenuItem>
						))}
					</TextField>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setFacebookPageModalOpen(false)}
						color="secondary"
						variant="outlined"
						disabled={loadingMetaAuth}
					>
						Cancelar
					</Button>
					<Button
						onClick={handleCompleteFacebookOAuth}
						color="primary"
						variant="contained"
						disabled={loadingMetaAuth || !selectedFacebookPageId}
					>
						Conectar
					</Button>
				</DialogActions>
			</Dialog>
			<MainHeader>
				<Title>{i18n.t("connections.title")}</Title>
				<MainHeaderButtonsWrapper>
					{!hideResellerControls && (
						<Can
							role={user.profile}
							perform="connections-page:manageConnections"
							yes={() => (
								<Button
									variant="contained"
									color="primary"
									onClick={handleOpenConnectionTypeMenu}
									disabled={loadingMetaAuth}
								>
									{i18n.t("connections.buttons.add")}
								</Button>
							)}
						/>
					)}
					<Menu
						anchorEl={connectionTypeAnchorEl}
						open={Boolean(connectionTypeAnchorEl)}
						onClose={handleCloseConnectionTypeMenu}
						getContentAnchorEl={null}
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						transformOrigin={{ vertical: "top", horizontal: "right" }}
					>
						{connectionTypeOptions
							.filter(option => canUseMetaConnections || !isMetaChannel(option.channel))
							.map(option => {
								const OptionIcon = option.Icon;

								return (
									<MenuItem
										key={option.channel}
										onClick={() => handleSelectConnectionType(option.channel)}
									>
										<ListItemIcon className={classes.connectionTypeIcon}>
											<OptionIcon className={classes.connectionTypeSvgIcon} />
										</ListItemIcon>
										<ListItemText
											className={classes.connectionTypeItemText}
											primary={
												<>
													{option.label}
													<Tooltip title={option.description}>
														<InfoOutlined fontSize="small" color="action" />
													</Tooltip>
												</>
											}
										/>
									</MenuItem>
								);
							})}
					</Menu>
				</MainHeaderButtonsWrapper>
			</MainHeader>
			<Paper className={classes.mainPaper} variant="outlined">
				<div className={classes.mobileCards}>
					{loading ? (
						<CircularProgress size={24} className={classes.buttonProgress} />
					) : (
						connections?.map(renderMobileConnectionCard)
					)}
				</div>

				<Table size="small" className={classes.tableWrapper}>
					<TableHead>
						<TableRow>
							<TableCell align="center">
								{i18n.t("connections.table.platform")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.name")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.connection")}
							</TableCell>
							<TableCell align="center">
								{i18n.t("connections.table.status")}
							</TableCell>
							{canManageConnections && (
								<TableCell align="center">
									{i18n.t("connections.table.usage")}
								</TableCell>
							)}
							{canManageConnections && (
								<TableCell align="center">
									{i18n.t("connections.table.session")}
								</TableCell>
							)}
							{isResellerCustomer && (
								<TableCell align="center">
									Vencimiento
								</TableCell>
							)}
							<TableCell align="center">
								{i18n.t("connections.table.lastUpdate")}
							</TableCell>
							{!hideResellerControls && (
								<TableCell align="center">
									{i18n.t("connections.table.default")}
								</TableCell>
							)}
							{canManageConnections && (
								<TableCell align="center">
									{i18n.t("connections.table.actions")}
								</TableCell>
							)}
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRowSkeleton
								columns={
									(canManageConnections ? 9 : 6) +
									(isResellerCustomer ? 1 : 0) -
									(hideResellerControls ? 1 : 0)
								}
							/>
						) : (
							<>
								{connections?.length > 0 &&
									connections.map(whatsApp => (
										<TableRow key={whatsApp.id}>
											<TableCell align="center">
												{renderPlatformCell(whatsApp)}
											</TableCell>
											<TableCell align="center">{whatsApp.name}</TableCell>
											<TableCell align="center">
												{getConnectionLabel(whatsApp)}
											</TableCell>
											<TableCell align="center">
												{renderStatusToolTips(whatsApp)}
											</TableCell>
											{canManageConnections && (
												<TableCell align="center">
													{renderUsageSelect(whatsApp)}
												</TableCell>
											)}
											{canManageConnections && (
												<TableCell align="center">
													{renderActionButtons(whatsApp)}
												</TableCell>
											)}
											{isResellerCustomer && (
												<TableCell align="center">
													{formatConnectionExpiration(whatsApp.expiresAt)}
												</TableCell>
											)}
											<TableCell align="center">
												{format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
											</TableCell>
											{!hideResellerControls && (
												<TableCell align="center">
													{whatsApp.isDefault && (
														<div className={classes.customTableCell}>
															<CheckCircle style={{ color: green[500] }} />
														</div>
													)}
												</TableCell>
											)}
											{canManageConnections && (
												<TableCell align="center">
													<IconButton
														size="small"
														disabled={isConnectionExpired(whatsApp)}
														onClick={() => handleEditWhatsApp(whatsApp)}
													>
														<Edit />
													</IconButton>

													{canDeleteConnections && (
														<IconButton
															size="small"
															disabled={isConnectionExpired(whatsApp)}
															onClick={() => {
																handleOpenConfirmationModal("delete", whatsApp.id);
															}}
														>
															<DeleteOutline />
														</IconButton>
													)}
												</TableCell>
											)}
										</TableRow>
									))}
							</>
						)}
					</TableBody>
				</Table>
			</Paper>
		</MainContainer>
	);
};

export default Connections;

